import {storeToRefs, type Pinia} from 'pinia';
import * as Sentry from '@sentry/nuxt';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
import {useCustomerProfileStore} from '~/stores/customerProfile';
import {useStoryblokDatasourcesStore} from '~/stores/storyblokDatasources';
import {useStoryblokFooterStore} from '~/stores/storyblokFooter';
import {useCrmStateStore} from '~/stores/crmStateStore';
import {useShoppingListsStore} from '~/stores/shoppingLists';
import {OidcService} from '~/openapi';
import {isUnauthorizedError} from '#shared/utils/unauthorizedError';

export default defineNuxtPlugin(async ({$pinia}) => {
	const hypermarketsStore = useStoryblokHypermarketsStore($pinia as Pinia);
	const datasourcesStore = useStoryblokDatasourcesStore($pinia as Pinia);
	const footerStore = useStoryblokFooterStore($pinia as Pinia);

	const {fetchAuthStatus} = useAuth();

	await Promise.all([
		hypermarketsStore.fetchHypermarkets(),
		datasourcesStore.fetchEntries('tags', 'sorting'),
		footerStore.fetchFooter(),
		fetchAuthStatus(),
	]);

	const customerProfileStore = useCustomerProfileStore($pinia as Pinia);
	const {userIsLogged} = storeToRefs(customerProfileStore);

	if (customerProfileStore.profile) {
		hypermarketsStore.setHypermarketFromProfile(customerProfileStore.profile);
	}

	const crmStateStore = useCrmStateStore();
	const shoppingListsStore = useShoppingListsStore($pinia as Pinia);
	const {handleUnauthorized} = useUnauthorizedPolicy();

	try {
		await shoppingListsStore.ensureSelectedListItems();
	} catch (e: unknown) {
		if (isUnauthorizedError(e)) {
			await handleUnauthorized();
		} else {
			Sentry.captureException(e, {tags: {area: 'init-shopping-lists'}});
		}
	}

	if (userIsLogged.value && crmStateStore.isUpdateProfileState()) {
		try {
			await OidcService.profileUpdate({});
			crmStateStore.clearState();
		} catch (e: any) {
			if (isUnauthorizedError(e)) {
				await handleUnauthorized();
			} else {
				throw createError({
					message: 'Failed to invalidate cache for customer profile',
					cause: e,
				});
			}
		}
	}
});
